import React, { useState, Component } from 'react';
import { SparklesIcon, LockIcon, PlayIcon, PlusIcon, TargetIcon, ZapIcon } from 'lucide-react';
import { Gate } from '../components/upsell/Gate';
import { UpsellBadge } from '../components/upsell/UpsellBadge';
import { ProTooltip } from '../components/upsell/ProTooltip';
import { ContextualPopup } from '../components/upsell/ContextualPopup';
import { PaywallOverlay } from '../components/upsell/PaywallOverlay';
import { UsageMeter } from '../components/UsageMeter';
import { CelebrateNudge } from '../components/upsell/CelebrateNudge';
import { useEntitlements } from '../hooks/useEntitlements';
export function UpsellDemoPage() {
  const entitlements = useEntitlements();
  const [showContextualPopup, setShowContextualPopup] = useState(false);
  const [showPaywallOverlay, setShowPaywallOverlay] = useState(false);
  const [showCelebrateNudge, setShowCelebrateNudge] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState<any>('forecast.bandit');
  const handleUpgrade = (plan: any) => {
    console.log('Upgrade to:', plan);
    alert(`Demo: Would upgrade to ${plan}`);
  };
  return <div className="w-full min-h-screen bg-gray-50" data-id="element-6407">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4" data-id="element-6408">
        <div className="flex items-center gap-3" data-id="element-6409">
          <SparklesIcon className="w-8 h-8 text-purple-600" data-id="element-6410" />
          <div data-id="element-6411">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-6412">
              Upsell Components Demo
            </h1>
            <p className="text-sm text-gray-600 mt-1" data-id="element-6413">
              Interactive showcase of product gating and upgrade flows
            </p>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 py-8" data-id="element-6414">
        {/* Current Plan Info */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-6 text-white mb-8" data-id="element-6415">
          <h2 className="text-xl font-bold mb-2" data-id="element-6416">Current Plan Status</h2>
          <div className="grid grid-cols-4 gap-4 text-sm" data-id="element-6417">
            <div data-id="element-6418">
              <p className="text-blue-100 mb-1" data-id="element-6419">Plan</p>
              <p className="text-lg font-bold" data-id="element-6420">{entitlements.plan}</p>
            </div>
            <div data-id="element-6421">
              <p className="text-blue-100 mb-1" data-id="element-6422">Programs</p>
              <p className="text-lg font-bold" data-id="element-6423">
                {entitlements.usage.programs} / {entitlements.limits.programs}
              </p>
            </div>
            <div data-id="element-6424">
              <p className="text-blue-100 mb-1" data-id="element-6425">Playbooks</p>
              <p className="text-lg font-bold" data-id="element-6426">
                {entitlements.usage.playbooks} /{' '}
                {entitlements.limits.playbooks === Infinity ? '∞' : entitlements.limits.playbooks}
              </p>
            </div>
            <div data-id="element-6427">
              <p className="text-blue-100 mb-1" data-id="element-6428">Plays</p>
              <p className="text-lg font-bold" data-id="element-6429">
                {entitlements.usage.plays} / {entitlements.limits.plays}
              </p>
            </div>
          </div>
        </div>
        {/* Contextual Examples */}
        <div className="space-y-8" data-id="element-6430">
          {/* Programs Page Context */}
          <div className="bg-white rounded-xl border border-gray-200 p-6" data-id="element-6431">
            <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-6432">
              Programs Page - Create Button Gating
            </h3>
            <div className="bg-gray-50 rounded-lg p-6 border border-gray-200" data-id="element-6433">
              <div className="flex items-center justify-between mb-4" data-id="element-6434">
                <div data-id="element-6435">
                  <h4 className="text-sm font-semibold text-gray-900" data-id="element-6436">
                    Active Programs (2/5)
                  </h4>
                  <p className="text-xs text-gray-600" data-id="element-6437">
                    You have 3 programs remaining on Growth plan
                  </p>
                </div>
                <Gate feature="programs.create" onFail={() => {
                setSelectedFeature('programs.create');
                setShowContextualPopup(true);
              }} data-id="element-6438">
                  <button className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors" data-id="element-6439">
                    <PlusIcon className="w-4 h-4" data-id="element-6440" />
                    Create Program
                  </button>
                </Gate>
              </div>
              <div className="grid grid-cols-2 gap-3" data-id="element-6441">
                <div className="p-4 bg-white rounded-lg border border-gray-200" data-id="element-6442">
                  <h5 className="text-sm font-semibold text-gray-900 mb-2" data-id="element-6443">
                    Q1 Enterprise Growth
                  </h5>
                  <p className="text-xs text-gray-600" data-id="element-6444">
                    8 playbooks • 24 plays
                  </p>
                </div>
                <div className="p-4 bg-white rounded-lg border border-gray-200" data-id="element-6445">
                  <h5 className="text-sm font-semibold text-gray-900 mb-2" data-id="element-6446">
                    PLG Activation
                  </h5>
                  <p className="text-xs text-gray-600" data-id="element-6447">
                    5 playbooks • 18 plays
                  </p>
                </div>
              </div>
            </div>
          </div>
          {/* Forecast Page Context */}
          <div className="bg-white rounded-xl border border-gray-200 p-6" data-id="element-6448">
            <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-6449">
              Forecast Page - Advanced Features
            </h3>
            <div className="bg-gray-50 rounded-lg p-6 border border-gray-200" data-id="element-6450">
              <div className="flex items-center justify-between mb-4" data-id="element-6451">
                <div data-id="element-6452">
                  <h4 className="text-sm font-semibold text-gray-900" data-id="element-6453">
                    Pipeline Forecast
                  </h4>
                  <p className="text-xs text-gray-600" data-id="element-6454">
                    $8.3M forecasted • 78% confidence
                  </p>
                </div>
                <div className="flex items-center gap-2" data-id="element-6455">
                  <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm" data-id="element-6456">
                    Export Basic
                  </button>
                  <ProTooltip feature="Advanced Export" plan="Growth" onSeePlan={() => {
                  setSelectedFeature('forecast.export_advanced');
                  setShowPaywallOverlay(true);
                }} data-id="element-6457">
                    <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-400 rounded-lg cursor-not-allowed text-sm" data-id="element-6458">
                      <LockIcon className="w-4 h-4" data-id="element-6459" />
                      Export Advanced
                    </button>
                  </ProTooltip>
                </div>
              </div>
              <div className="space-y-3" data-id="element-6460">
                <div className="flex items-center justify-between" data-id="element-6461">
                  <span className="text-sm text-gray-700" data-id="element-6462">Bandit Testing</span>
                  <Gate feature="forecast.bandit" onFail={() => {
                  setSelectedFeature('forecast.bandit');
                  setShowContextualPopup(true);
                }} data-id="element-6463">
                    <button className="text-sm text-red-600 hover:text-red-700 font-medium" data-id="element-6464">
                      Run Test
                    </button>
                  </Gate>
                  {!entitlements.flags['forecast.bandit'] && <UpsellBadge plan="Scale" size="sm" data-id="element-6465" />}
                </div>
              </div>
            </div>
          </div>
          {/* AI Configuration Context */}
          <div className="bg-white rounded-xl border border-gray-200 p-6" data-id="element-6466">
            <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-6467">
              AI Configuration - Copilot Tasks
            </h3>
            <div className="bg-gray-50 rounded-lg p-6 border border-gray-200" data-id="element-6468">
              <div className="flex items-center justify-between mb-4" data-id="element-6469">
                <div data-id="element-6470">
                  <h4 className="text-sm font-semibold text-gray-900" data-id="element-6471">
                    Automation Tasks
                  </h4>
                  <p className="text-xs text-gray-600" data-id="element-6472">
                    Reduce time to next stage with AI
                  </p>
                </div>
                <div className="flex items-center gap-2" data-id="element-6473">
                  <UpsellBadge plan="Scale" size="sm" data-id="element-6474" />
                </div>
              </div>
              <div className="relative" data-id="element-6475">
                <div className="absolute inset-0 bg-gradient-to-b from-transparent to-white z-10 flex items-center justify-center" data-id="element-6476">
                  <button onClick={() => {
                  setSelectedFeature('copilot.tasks');
                  setShowPaywallOverlay(true);
                }} className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium shadow-lg" data-id="element-6477">
                    Unlock Copilot Tasks
                  </button>
                </div>
                <div className="opacity-40 pointer-events-none space-y-2" data-id="element-6478">
                  <div className="p-3 bg-white rounded-lg border border-gray-200" data-id="element-6479">
                    <div className="flex items-center gap-3" data-id="element-6480">
                      <ZapIcon className="w-4 h-4 text-gray-400" data-id="element-6481" />
                      <div className="flex-1" data-id="element-6482">
                        <p className="text-sm font-medium text-gray-900" data-id="element-6483">
                          Auto-research accounts
                        </p>
                        <p className="text-xs text-gray-600" data-id="element-6484">
                          Gather intel before outreach
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="p-3 bg-white rounded-lg border border-gray-200" data-id="element-6485">
                    <div className="flex items-center gap-3" data-id="element-6486">
                      <TargetIcon className="w-4 h-4 text-gray-400" data-id="element-6487" />
                      <div className="flex-1" data-id="element-6488">
                        <p className="text-sm font-medium text-gray-900" data-id="element-6489">
                          Personalize messaging
                        </p>
                        <p className="text-xs text-gray-600" data-id="element-6490">
                          Tailor content to persona
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Integrations Context */}
          <div className="bg-white rounded-xl border border-gray-200 p-6" data-id="element-6491">
            <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-6492">
              Integrations - Connection Limits
            </h3>
            <div className="bg-gray-50 rounded-lg p-6 border border-gray-200" data-id="element-6493">
              <UsageMeter variant="inline" onUpgrade={handleUpgrade} data-id="element-6494" />
              <div className="mt-4 grid grid-cols-3 gap-3" data-id="element-6495">
                <div className="p-3 bg-white rounded-lg border border-green-200" data-id="element-6496">
                  <div className="flex items-center justify-between mb-2" data-id="element-6497">
                    <span className="text-sm font-medium text-gray-900" data-id="element-6498">
                      Salesforce
                    </span>
                    <span className="text-xs text-green-600 font-medium" data-id="element-6499">
                      Connected
                    </span>
                  </div>
                </div>
                <div className="p-3 bg-white rounded-lg border border-green-200" data-id="element-6500">
                  <div className="flex items-center justify-between mb-2" data-id="element-6501">
                    <span className="text-sm font-medium text-gray-900" data-id="element-6502">
                      HubSpot
                    </span>
                    <span className="text-xs text-green-600 font-medium" data-id="element-6503">
                      Connected
                    </span>
                  </div>
                </div>
                <div className="p-3 bg-white rounded-lg border border-gray-200 opacity-50" data-id="element-6504">
                  <div className="flex items-center justify-between mb-2" data-id="element-6505">
                    <span className="text-sm font-medium text-gray-900" data-id="element-6506">
                      Marketo
                    </span>
                    <LockIcon className="w-4 h-4 text-gray-400" data-id="element-6507" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Component Library */}
          <div className="bg-white rounded-xl border border-gray-200 p-6" data-id="element-6508">
            <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-6509">
              Component Triggers
            </h3>
            <div className="flex flex-wrap gap-3" data-id="element-6510">
              <button onClick={() => {
              setSelectedFeature('forecast.bandit');
              setShowContextualPopup(true);
            }} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" data-id="element-6511">
                Show Contextual Popup
              </button>
              <button onClick={() => {
              setSelectedFeature('copilot.tasks');
              setShowPaywallOverlay(true);
            }} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors" data-id="element-6512">
                Show Paywall Overlay
              </button>
              <button onClick={() => setShowCelebrateNudge(true)} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors" data-id="element-6513">
                Show Celebrate Nudge
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Demo Components */}
      <ContextualPopup isOpen={showContextualPopup} onClose={() => setShowContextualPopup(false)} intent="pro_click" feature={selectedFeature} onUpgrade={handleUpgrade} onComparePlans={() => {
      setShowContextualPopup(false);
      alert('Demo: Would show plan comparison');
    }} data-id="element-6514" />
      <PaywallOverlay isOpen={showPaywallOverlay} onClose={() => setShowPaywallOverlay(false)} feature={selectedFeature} onUpgrade={handleUpgrade} data-id="element-6515" />
      <CelebrateNudge isVisible={showCelebrateNudge} onClose={() => setShowCelebrateNudge(false)} onUpgrade={() => {
      setShowCelebrateNudge(false);
      handleUpgrade('Growth');
    }} message="You've created your first program! Upgrade to create more and unlock advanced features." data-id="element-6516" />
      <UsageMeter variant="footer" onUpgrade={handleUpgrade} data-id="element-6517" />
    </div>;
}